<template>
	<div
		class="app-button"
		:class="{
			'app-button--active': active,
			'app-button--configured': configured && !active,
		}"
		role="button"
		tabindex="0"
		:title="tooltip"
		@click="buttonClicked"
		@keydown.enter.prevent="buttonClicked"
		@keydown.space.prevent="buttonClicked"
	>
		<span class="app-button__icon">
			<img
				v-if="showIcon"
				:src="iconPath"
				:alt="`Ícono de ${item.name}`"
				@error="markIconAsBroken"
			>
			<span v-else class="app-button__initials">{{ initials }}</span>
			<i v-if="configured" class="app-button__check" :title="stateText">
				<v-icon>check</v-icon>
			</i>
		</span>
		<span class="app-button__copy">
			<strong>{{ item.name }}</strong>
			<small v-if="subtitle">{{ subtitle }}</small>
		</span>
		<span v-if="filterIcons.length" class="app-button__actions" @click.stop>
			<app-tooltip-button
				v-for="(icon, indexIcon) in filterIcons"
				:key="indexIcon"
				:icon="icon.name"
				:color="dinamicColor(icon)"
				:text="icon.text"
				@click-icon="iconAction(icon)"
			/>
		</span>
		<v-icon class="app-button__chevron">chevron_right</v-icon>
	</div>
</template>
<script>
import appTooltipButton from '@/components/shared/app-tooltip-button';
import lib from '@/lib';

const { matchBetweenArrays } = lib;

function filterIcons() {
	return matchBetweenArrays([this.iconsToUse], [this.icons, 'flag']);
}

function dinamicColor({ color, flag }) {
	if (flag === 'edit') {
		return this.active ? 'white' : color;
	}
	return color;
}

function showIcon() {
	return Boolean(this.iconPath) && !this.iconBroken;
}

function initials() {
	const words = String(this.item.name || '')
		.split(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ]+/)
		.filter(word => word);
	if (!words.length) return '?';
	const letters = words.length > 1 ?
		`${words[0][0]}${words[1][0]}` : words[0].substring(0, 2);
	return letters.toUpperCase();
}

function tooltip() {
	const name = this.item.name || '';
	return this.subtitle ? `${name} · ${this.subtitle}` : name;
}

function markIconAsBroken() {
	this.iconBroken = true;
}

function buttonClicked() {
	this.$emit('button-clicked', this.item);
}

function iconAction(icon) {
	const { name } = icon;
	const opt = {
		edit: 'edit-button',
		highlight_off: 'erase-configuration',
	};
	this.$emit(opt[name], this.item);
}

function watchIconPath() {
	this.iconBroken = false;
}

function data() {
	return {
		iconBroken: false,
		icons: [
			{ name: 'edit', color: 'success', text: 'editar', flag: 'edit' },
			{ name: 'highlight_off', color: 'error', text: 'borrar configuración', flag: 'close' },
		],
	};
}

export default {
	name: 'app-button',
	components: {
		appTooltipButton,
	},
	computed: {
		filterIcons,
		initials,
		showIcon,
		tooltip,
	},
	data,
	methods: {
		buttonClicked,
		dinamicColor,
		iconAction,
		markIconAsBroken,
	},
	props: {
		active: {
			default: false,
			type: Boolean,
		},
		configured: {
			default: false,
			type: Boolean,
		},
		iconPath: {
			default: '',
			type: String,
		},
		item: {
			default: () => ({}),
			type: Object,
		},
		iconsToUse: {
			default: () => [],
			type: Array,
		},
		stateText: {
			default: 'Configurado',
			type: String,
		},
		subtitle: {
			default: '',
			type: String,
		},
	},
	watch: {
		iconPath: watchIconPath,
	},
};
</script>
<style lang="scss" scoped>
.app-button {
	align-items: center;
	background: #fff;
	border: 1px solid #e2e9f0;
	border-radius: 10px;
	cursor: pointer;
	display: flex;
	gap: 10px;
	min-height: 54px;
	min-width: 0;
	outline: none;
	padding: 8px 8px 8px 10px;
	transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;

	&:hover {
		border-color: #c2d4e4;
		box-shadow: 0 4px 12px rgba(31, 57, 85, .08);
		transform: translateY(-1px);
	}

	&:focus {
		border-color: #1a4338;
		box-shadow: 0 0 0 3px rgba(26, 67, 56, .12);
	}
}

.app-button__icon {
	align-items: center;
	background: linear-gradient(135deg, #2b435b 0%, #3f6187 100%);
	border-radius: 9px;
	display: flex;
	flex: 0 0 auto;
	height: 34px;
	justify-content: center;
	position: relative;
	width: 34px;

	img { height: 21px; object-fit: contain; width: 21px; }
}

.app-button__initials {
	color: rgba(255, 255, 255, .9);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: .5px;
}

.app-button__check {
	align-items: center;
	background: #2eaa64;
	border: 2px solid #fff;
	border-radius: 50%;
	bottom: -4px;
	display: flex;
	height: 16px;
	justify-content: center;
	position: absolute;
	right: -5px;
	width: 16px;

	.icon { color: #fff; font-size: 10px; }
}

.app-button__copy {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	min-width: 0;
	text-align: left;

	strong {
		color: #2b435b;
		font-size: 13px;
		font-weight: 600;
		line-height: 1.25;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	small {
		color: #8495a5;
		font-size: 10px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.app-button__actions {
	align-items: center;
	display: flex;
	flex: 0 0 auto;
}

.app-button__chevron {
	color: #c2ccd6;
	flex: 0 0 auto;
	font-size: 19px;
}

.app-button--configured {
	background: #f5faf7;
	border-color: #b9dccb;

	.app-button__copy strong { color: #1f4c3b; }
}

.app-button--active {
	background: linear-gradient(135deg, #1f5546 0%, #1a4338 100%);
	border-color: #1a4338;
	box-shadow: 0 6px 16px rgba(26, 67, 56, .24);

	.app-button__icon { background: rgba(255, 255, 255, .16); }
	.app-button__check { background: #6fd39b; border-color: #1a4338; }
	.app-button__copy strong { color: #fff; }
	.app-button__copy small { color: rgba(255, 255, 255, .66); }
	.app-button__chevron { color: rgba(255, 255, 255, .7); }

	&:hover { border-color: #2e6b59; }
}

@media (max-width: 700px) {
	.app-button { min-height: 50px; padding: 7px; }
	.app-button__chevron { display: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/app-button.vue